import { Component, Element, Event, EventEmitter, Listen, Method, Prop, Watch, h } from '@stencil/core';
import { HTMLStencilElement } from '@stencil/core/internal';
import Bind from 'bind-decorator';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { createMutationObserver } from '../../utils/mutation-observer';
import { LineType, StepStatus } from './types';

export interface NavigationEventDetail {
	step: number;
}

@Component({
	shadow: true,
	styleUrl: 'zds-stepper.scss',
	tag: 'zds-stepper',
})
export class ZdsStepper {
	activeStepChangeTimeout: NodeJS.Timeout = null;
	mutationObserver: MutationObserver;
	steps: HTMLZdsStepElement[] = null;

	@Element() hostElement: HTMLStencilElement;

	@Event({ bubbles: true }) zdsStepperNavigate: EventEmitter<NavigationEventDetail>;

	@Prop() activeStep: number = 0;
	@Prop() nonLinear: boolean;
	@Prop() variant: 'horizontal' | 'vertical' = 'horizontal';

	@Method()
	async activateStep(stepIndex: number) {
		if (stepIndex < 0 || stepIndex > this.steps.length - 1) {
			console.error(
				`zds-stepper: trying to navigate to an out-of-bounds step index: ${stepIndex} (registered steps: 0 - ${
					this.steps.length - 1
				})`,
			);
			return;
		}

		this.activeStep = stepIndex;
	}

	@Method()
	async setStepStatus(stepIndex: number, newStatus: StepStatus) {
		if (!this.checkIfStepIndexValid(stepIndex)) return;

		await this.steps[stepIndex].changeState({
			status: newStatus,
		});

		this.syncStepAttributes(false);
	}

	@Watch('activeStep')
	onActiveStepChange(newValue: number, oldValue: number) {
		this.syncStepAttributes(false);
		this.steps[oldValue].changeState({
			active: false,
		});

		// When the animation is done, make the new step active and emit `zdsStepperNavigate` event
		if (this.activeStepChangeTimeout) {
			clearTimeout(this.activeStepChangeTimeout);
		}
		this.activeStepChangeTimeout = setTimeout(() => {
			this.steps[newValue].changeState({
				active: true,
			});
			this.zdsStepperNavigate.emit({ step: newValue });
		}, 300);
	}

	@Listen('zdsActivate')
	onZdsActivate(event: CustomEvent) {
		this.activeStep = event.detail.index;
	}

	checkIfStepIndexValid(stepIndex: number): boolean {
		if (stepIndex >= 0 && stepIndex < this.steps.length) return true;

		console.error(
			`zds-stepper: step index ${stepIndex} not round (registered steps: 0 - ${this.steps.length - 1})`,
		);
		return false;
	}

	componentWillLoad() {
		this.steps = Array.from(this.hostElement.querySelectorAll('zds-step'));
		this.syncStepAttributes();
	}

	connectedCallback() {
		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation);
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
	}

	@Bind
	handleMutation() {
		this.syncStepAttributes();
	}

	render() {
		const stepperClasses = generateCssClasses('zds-stepper', {
			[this.variant]: true,
		});

		return (
			<div class={stepperClasses}>
				<slot />
			</div>
		);
	}

	syncStepAttributes(includeActive: boolean = true) {
		if (!this.steps) {
			return;
		}

		this.steps.forEach((step, index) => {
			const disabledAttribute = step.getAttribute('disabled');
			const stepDisabledByAttribute = disabledAttribute !== null && disabledAttribute !== 'false';

			let lineType: LineType;
			if (index < this.activeStep) {
				lineType = 'solid';
			} else if (index < this.steps.length - 1) {
				lineType = 'dotted';
			} else {
				lineType = 'none';
			}

			const disabled =
				stepDisabledByAttribute || (!this.nonLinear && index >= this.activeStep && step.status === 'unvisited');

			const active = includeActive ? index === this.activeStep : undefined;

			step.changeState({
				active,
				disabled,
				lineType,
				index,
				stepperVariant: this.variant,
			});
		});
	}
}
